import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import EditIcon from '@mui/icons-material/Edit';
import { useFirebase } from '../../context/FirebaseContext';
import {
  getOAuthClientsV2,
  createOAuthClientV2,
  updateOAuthClientV2,
  deleteOAuthClientV2,
  regenerateOAuthClientSecretV2
} from '../../api/admin';

const OAuthClientsTab = () => {
  const { auth } = useFirebase();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClientSecret, setNewClientSecret] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    clientId: '',  // Optional custom client_id
    name: '',
    description: '',
    redirectUris: '',
    scopes: 'validation'
  });
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editRedirectUris, setEditRedirectUris] = useState('');

  const fetchClients = useCallback(() => {
    setLoading(true);
    setError(null);
    getOAuthClientsV2(auth, (data) => {
      setLoading(false);
      if (data?.error) {
        setError(data.error);
        return;
      }
      setClients(data?.clients || []);
    });
  }, [auth]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreateClient = () => {
    setFormLoading(true);
    setFormError(null);

    const redirectUris = formData.redirectUris
      .split('\n')
      .map(uri => uri.trim())
      .filter(uri => uri.length > 0);

    const scopes = formData.scopes
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const clientData = {
      name: formData.name,
      description: formData.description,
      redirectUris,
      scopes
    };
    // Only include clientId if provided
    if (formData.clientId.trim()) {
      clientData.clientId = formData.clientId.trim();
    }

    createOAuthClientV2(auth, (data) => {
      setFormLoading(false);
      if (data?.error || data?.message?.includes('already exists')) {
        setFormError(data.error || data.message);
        return;
      }
      setNewClientSecret({
        clientId: data.clientId,
        clientSecret: data.clientSecret
      });
      setCreateDialogOpen(false);
      setSecretDialogOpen(true);
      setFormData({ clientId: '', name: '', description: '', redirectUris: '', scopes: 'validation' });
      fetchClients();
    }, clientData);
  };

  const handleToggleStatus = (clientId, currentStatus) => {
    updateOAuthClientV2(auth, (data) => {
      if (data?.error) {
        setError(data.error);
        return;
      }
      fetchClients();
    }, clientId, { isActive: !currentStatus });
  };

  const handleRegenerateSecret = (clientId) => {
    regenerateOAuthClientSecretV2(auth, (data) => {
      if (data?.error) {
        setError(data.error);
        return;
      }
      setNewClientSecret({
        clientId: data.clientId,
        clientSecret: data.clientSecret
      });
      setSecretDialogOpen(true);
    }, clientId);
  };

  const handleDeleteClient = () => {
    deleteOAuthClientV2(auth, (data) => {
      if (data?.error) {
        setError(data.error);
        return;
      }
      setDeleteDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    }, selectedClient);
  };

  const handleOpenEditDialog = (client) => {
    setEditingClient(client);
    const uris = parseJsonArray(client.redirectUris);
    setEditRedirectUris(uris.join('\n'));
    setEditDialogOpen(true);
  };

  const handleSaveRedirectUris = () => {
    const redirectUris = editRedirectUris
      .split('\n')
      .map(uri => uri.trim())
      .filter(uri => uri.length > 0);

    updateOAuthClientV2(auth, (data) => {
      if (data?.error) {
        setError(data.error);
        return;
      }
      setEditDialogOpen(false);
      setEditingClient(null);
      fetchClients();
    }, editingClient.clientId, { redirectUris });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const parseJsonArray = (jsonStr) => {
    try {
      return JSON.parse(jsonStr || '[]');
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">OAuth Clients</Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchClients}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Client
          </Button>
        </Box>
      </Box>

      {/* Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        OAuth clients allow external applications (like ChatGPT, Claude) to authenticate users via OAuth 2.0.
        The client secret is shown only once when created - save it securely!
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <div className="admin-cards-mobile">
          {clients.length === 0 ? (
            <Typography color="text.secondary" py={4} textAlign="center">
              No OAuth clients configured. Create one to enable ChatGPT integration.
            </Typography>
          ) : (
            clients.map((client) => (
              <div className="admin-mobile-card" key={client.clientId}>
                <div className="admin-mobile-card-header">
                  <Typography fontWeight={500}>{client.name}</Typography>
                  {client.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {client.description}
                    </Typography>
                  )}
                </div>
                <div className="admin-mobile-card-body">
                  <div className="admin-mobile-card-row">
                    <span className="admin-mobile-card-row-label">Client ID</span>
                    <span className="admin-mobile-card-row-value">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{client.clientId}</code>
                        <Tooltip title="Copy Client ID">
                          <IconButton size="small" onClick={() => copyToClipboard(client.clientId)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </span>
                  </div>
                  <div className="admin-mobile-card-row">
                    <span className="admin-mobile-card-row-label">Redirect URIs</span>
                    <span className="admin-mobile-card-row-value">
                      <div className="admin-mobile-card-chips">
                        {parseJsonArray(client.redirectUris).map((uri, idx) => (
                          <Chip
                            key={idx}
                            label={uri}
                            size="small"
                            sx={{ maxWidth: '100%' }}
                          />
                        ))}
                      </div>
                    </span>
                  </div>
                  <div className="admin-mobile-card-row">
                    <span className="admin-mobile-card-row-label">Scopes</span>
                    <span className="admin-mobile-card-row-value">
                      <div className="admin-mobile-card-chips">
                        {parseJsonArray(client.scopes).map((scope, idx) => (
                          <Chip key={idx} label={scope} size="small" color="primary" variant="outlined" />
                        ))}
                      </div>
                    </span>
                  </div>
                  <div className="admin-mobile-card-row">
                    <span className="admin-mobile-card-row-label">Status</span>
                    <span className="admin-mobile-card-row-value">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={client.isActive}
                            onChange={() => handleToggleStatus(client.clientId, client.isActive)}
                            size="small"
                          />
                        }
                        label={client.isActive ? 'Active' : 'Inactive'}
                      />
                    </span>
                  </div>
                  <div className="admin-mobile-card-row">
                    <span className="admin-mobile-card-row-label">Created</span>
                    <span className="admin-mobile-card-row-value">
                      {new Date(client.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="admin-mobile-card-actions">
                  <Tooltip title="Edit Redirect URIs">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(client)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Regenerate Secret">
                    <IconButton
                      size="small"
                      onClick={() => handleRegenerateSecret(client.clientId)}
                      color="warning"
                    >
                      <KeyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Client">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedClient(client.clientId);
                        setDeleteDialogOpen(true);
                      }}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Clients Table (Desktop) */}
      <div className="admin-table-desktop">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Client ID</TableCell>
                <TableCell>Redirect URIs</TableCell>
                <TableCell>Scopes</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={4}>
                      No OAuth clients configured. Create one to enable ChatGPT integration.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow key={client.clientId}>
                    <TableCell>
                      <Typography fontWeight={500}>{client.name}</Typography>
                      {client.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {client.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <code style={{ fontSize: '0.75rem' }}>{client.clientId}</code>
                        <Tooltip title="Copy Client ID">
                          <IconButton size="small" onClick={() => copyToClipboard(client.clientId)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {parseJsonArray(client.redirectUris).map((uri, idx) => (
                        <Chip
                          key={idx}
                          label={uri}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5, maxWidth: 200 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      {parseJsonArray(client.scopes).map((scope, idx) => (
                        <Chip key={idx} label={scope} size="small" color="primary" variant="outlined" sx={{ mr: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={client.isActive}
                            onChange={() => handleToggleStatus(client.clientId, client.isActive)}
                            size="small"
                          />
                        }
                        label={client.isActive ? 'Active' : 'Inactive'}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(client.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Redirect URIs">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(client)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Regenerate Secret">
                        <IconButton
                          size="small"
                          onClick={() => handleRegenerateSecret(client.clientId)}
                          color="warning"
                        >
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Client">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedClient(client.clientId);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Create Client Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create OAuth Client</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Tip:</strong> Leave Client ID empty to auto-generate, or enter a custom ID
            (e.g., copy from ChatGPT callback URL if it contains the client_id you want to use).
          </Alert>
          <TextField
            fullWidth
            label="Client ID (Optional)"
            placeholder="Leave empty to auto-generate"
            value={formData.clientId}
            onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
            margin="normal"
            helperText="Custom client ID or leave empty for auto-generated one"
          />
          <TextField
            fullWidth
            label="Name"
            placeholder="e.g., ChatGPT Integration"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            placeholder="Optional description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Redirect URIs"
            placeholder="https://chat.openai.com/aip/g-xxx/oauth/callback"
            value={formData.redirectUris}
            onChange={(e) => setFormData({ ...formData, redirectUris: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            helperText="One URI per line. Get this from ChatGPT's GPT Action settings."
            required
          />
          <TextField
            fullWidth
            label="Scopes"
            placeholder="validation, read"
            value={formData.scopes}
            onChange={(e) => setFormData({ ...formData, scopes: e.target.value })}
            margin="normal"
            helperText="Comma-separated list of scopes"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateClient}
            variant="contained"
            disabled={!formData.name || !formData.redirectUris || formLoading}
          >
            {formLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secret Display Dialog */}
      <Dialog open={secretDialogOpen} onClose={() => setSecretDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Client Credentials</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Save these credentials now! The client secret cannot be retrieved later.
          </Alert>

          <Typography variant="subtitle2" gutterBottom>Client ID</Typography>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <code>{newClientSecret?.clientId}</code>
            <IconButton onClick={() => copyToClipboard(newClientSecret?.clientId)}>
              <ContentCopyIcon />
            </IconButton>
          </Paper>

          <Typography variant="subtitle2" gutterBottom>Client Secret</Typography>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'warning.light' }}>
            <code style={{ wordBreak: 'break-all' }}>{newClientSecret?.clientSecret}</code>
            <IconButton onClick={() => copyToClipboard(newClientSecret?.clientSecret)}>
              <ContentCopyIcon />
            </IconButton>
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Use these credentials in ChatGPT's GPT Action OAuth configuration:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li><strong>Authorization URL:</strong> https://api.nanotradingcopilot.com/oauth/authorize</li>
            <li><strong>Token URL:</strong> https://api.nanotradingcopilot.com/oauth/token</li>
            <li><strong>Scope:</strong> validation</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecretDialogOpen(false)} variant="contained">
            I've Saved the Secret
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete OAuth Client?</DialogTitle>
        <DialogContent>
          <Typography>
            This will revoke all tokens and prevent the application from accessing your API.
            Users will need to re-authenticate.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteClient} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Redirect URIs Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Redirect URIs</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Update the callback URLs for <strong>{editingClient?.name}</strong>.
            This is useful when ChatGPT generates a new callback URL.
          </Alert>
          <Typography variant="subtitle2" gutterBottom>Client ID</Typography>
          <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
            <code>{editingClient?.clientId}</code>
          </Paper>
          <TextField
            fullWidth
            label="Redirect URIs"
            placeholder="https://chat.openai.com/aip/g-xxx/oauth/callback"
            value={editRedirectUris}
            onChange={(e) => setEditRedirectUris(e.target.value)}
            margin="normal"
            multiline
            rows={4}
            helperText="One URI per line. Get this from ChatGPT's GPT Action settings."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRedirectUris}
            variant="contained"
            disabled={!editRedirectUris.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OAuthClientsTab;
